import { loginManager } from "@/services/security/login-manager";
import axios from "axios";
import { platformManager } from "@/services/platform/platform-manager";
import { errorStore, formStore, mainStore, themeStore } from "@/main";
import { useMainStore } from "@/store/main/main";
import { usePlatformStore } from "@/store/platform/platform";
import { colorManager } from "@/services/platform/color-manager";
import { pictureManager } from "@/services/utilities/picture-manager";
import { errorManager } from "@/services/error/error-manager";
import { extractMutationVerdict } from "@/services/form/validation-errors";

const MUTATING_METHODS = ["post", "put", "delete"];

class ApiConnection {
  token = "";
  calling = null;
  callingMethod = null;
  abortedCalls = [];

  get(path, params = {}, security = true, cancelable = true, silent = false) {
    // Sérialise les paramètres tableau en `key[]=v1&key[]=v2` (convention Symfony), au lieu de
    // laisser URLSearchParams les convertir en chaîne unique "v1,v2" (ex: status: [2, 5]).
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => urlParams.append(key + "[]", item));
      } else {
        urlParams.append(key, value);
      }
    });
    var url = loginManager.apiPath + "/" + themeStore.locale + path;
    if (Object.keys(params).length) {
      url += "?" + urlParams.toString();
    }
    return this.fetchApi("get", url, null, security, false, cancelable, false, null, silent);
  }

  post(path, params = {}, security = true, cancelable = true, timeout = null, silent = false) {
    const url = loginManager.apiPath + "/" + themeStore.locale + path;
    return this.fetchApi("post", url, params, security, false, cancelable, false, timeout, silent);
  }

  put(path, params = {}, security = true, cancelable = true) {
    const url = loginManager.apiPath + "/" + themeStore.locale + path;
    return this.fetchApi("put", url, params, security, false, cancelable);
  }

  patch(path, params = {}, security = true, cancelable = true) {
    const url = loginManager.apiPath + "/" + themeStore.locale + path;
    return this.fetchApi("patch", url, params, security, false, cancelable);
  }

  delete(path, params = {}, security = true, cancelable = true) {
    const url = loginManager.apiPath + "/" + themeStore.locale + path;
    return this.fetchApi("delete", url, params, security, false, cancelable);
  }

  fetchApi(
    method,
    path,
    params,
    security = true,
    platformFetch = false,
    cancelable = true,
    isJwtRetry = false,
    timeout = null,
    silent = false,
  ) {
    const headers = {};
    const magicLink = this.getMagicLinkToken();
    const magicLinkResend = path.includes("magic-link/resend");

    if (magicLinkResend) {
      headers["Resend-Link"] = magicLink;
    }

    headers["Content-Type"] = "application/json;charset=utf-8";
    headers["Access-Control-Allow-Origin"] = "*";

    if (!loginManager.getApiPath() && !loginManager.apiPath && !platformFetch) {
      this.abortedCalls.push({
        method: method,
        path: path,
        params: params,
        security: security,
      });
      return (
        platformManager
          .loadPlatform()
          .then((platform) => {
            if (platform && platform.hostname && platform.name) {
              loginManager.setApiPath(platform);
              usePlatformStore().setPlatform(platform);
              if (magicLink) {
                window.location.reload();
              } else {
                const callNumber = Object.keys(this.abortedCalls).length;
                if (Object.keys(this.abortedCalls).length) {
                  let i = 0;
                  Object.values(this.abortedCalls).forEach((call) => {
                    this.fetchApi(call.method, loginManager.apiPath + call.path, call.params, call.security).then(
                      (res) => {
                        i++;

                        if (call.path.includes("/app/design") && res) {
                          const design = res && res.design ? res.design : res;
                          if (design) {
                            usePlatformStore().setDesign(design);
                            colorManager.setPlatformStyle(null, design);
                            pictureManager.preloadImages(design);
                          }
                          if (res.modules !== undefined) {
                            usePlatformStore().setModulesAvailable(res.modules);
                          }
                          if (res.dataVersionIsActive !== undefined) {
                            usePlatformStore().setDataVersionIsActive(res.dataVersionIsActive);
                          }
                          if (res.guides && res.guides.length) {
                            usePlatformStore().setGuides(res.guides);
                          }
                        }

                        if (call.path.includes("redirect/short-link-reply")) {
                          if (res && res.redirectLink) {
                            window.location.href = res.redirectLink;
                          }
                        }

                        if (i === callNumber) {
                          useMainStore().setAppLoading(false);
                        }
                      },
                    );
                  });
                }
              }
            }
          })
          // Plateforme en maintenance (503) : errorManager a déjà routé vers l'écran dédié.
          // On absorbe le rejet et on vide les appels annulés en attente : sans ce catch,
          // chaque appel API tenté pendant la maintenance produirait un unhandledrejection
          // remonté à Sentry, et les appels annulés seraient rejoués en sortie de maintenance.
          .catch(() => {
            this.abortedCalls = [];
          })
      );
    } else {
      // Magic-Link/Magic-Origin indépendants de `security` (revue Thibault, SecureDataView) :
      // un appel public (security=false, capability URL) doit pouvoir transmettre un lien magique
      // sans pour autant poser Authorization avec un JWT local potentiellement périmé, qui
      // déclencherait refresh puis logout sur une page anonyme (même garde-fou que magicLinkResend
      // ci-dessus, déjà posé hors du `if (security)`).
      if (magicLink && !magicLinkResend) {
        const magicOrigin = this.getMagicOrigin();
        headers["Magic-Link"] = magicLink;
        headers["Magic-Origin"] = magicOrigin;
      }

      if (security) {
        if (loginManager.token) {
          headers["Authorization"] = "Bearer " + loginManager.token;
        }
      }

      const store = useMainStore();
      const cancelToken = axios.CancelToken;
      let source;
      if (cancelable) {
        if (
          store.callingPath &&
          store.callingPath === path.split("?")[0] &&
          store.callingMethod &&
          store.callingMethod === method
        ) {
          this.source.cancel();
        }

        store.setCallingPath(path.split("?")[0]);
        store.setCallingMethod(method);
        this.source = cancelToken.source();
        source = this.source;
      } else {
        source = cancelToken.source();
      }

      let finalPath = path;
      if (this.isXdebugActive()) {
        const separator = path.includes("?") ? "&" : "?";
        finalPath += `${separator}XDEBUG_SESSION_START=1`;
      }

      // ModalFormComponent reste ouverte tant qu'elle n'a pas le verdict de la mutation déclenchée
      // par son submit : sans ce suivi elle se ferme avant l'arrivée des validationErrors Symfony.
      // Limité aux méthodes mutantes pour ne pas confondre le verdict avec un `get` de fond.
      const mutationHandle = MUTATING_METHODS.includes(method) ? formStore.startMutation(timeout) : null;

      const call = axios({
        method: method,
        url: finalPath,
        data: params,
        headers: headers,
        origin: "*",
        // Indispensable dans les deux sens depuis ST143-TI-18073 : l'hôte API est une origine
        // distincte de l'hôte front, donc sans cette option le navigateur ignore aussi
        // silencieusement le `Set-Cookie` du refresh token en réponse — pas seulement son envoi.
        withCredentials: true,
        cancelToken: source.token,
        timeout: timeout || undefined,
      });

      return new Promise((resolve, reject) => {
        call
          .then((response) => {
            mainStore.setInternet(true);
            this.calling = null;
            this.callingMethod = null;
            errorStore.setError(false);
            formStore.endMutation(mutationHandle, extractMutationVerdict(response.data));
            resolve(response.data);
          })
          .catch((error) => {
            this.calling = null;
            this.callingMethod = null;

            // Un timeout explicitement configuré (ex: upload de fichier) sort en ECONNABORTED,
            // code sinon confondu avec une annulation volontaire (source.cancel()) et donc
            // silencieusement ignoré par errorManager.dispatchError — la promesse resterait
            // en attente indéfiniment côté appelant sans ce cas dédié.
            if (timeout && error.code === "ECONNABORTED") {
              formStore.endMutation(mutationHandle, { reason: "timeout" });
              reject({ ...error, isTimeout: true });
              return;
            }

            errorManager.dispatchError(error, resolve, reject, {
              method,
              path,
              params,
              security,
              platformFetch,
              cancelable,
              isJwtRetry,
              silent,
              mutationHandle,
            });
          });
      });
    }
  }

  getMagicLinkToken() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("magic-link")) {
      return urlParams.get("magic-link");
    }
    return null;
  }

  getMagicOrigin() {
    return window.location.host + window.location.pathname;
  }

  // Ajoute cette méthode pour détecter Xdebug Helper
  isXdebugActive() {
    return document.cookie.includes("XDEBUG_SESSION");
  }
}

export const apiConnection = new ApiConnection();
